import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Calendar, User, FileText } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";

const testTypeColors = {
  complete_blood_count: "bg-red-100 text-red-700",
  basic_metabolic_panel: "bg-blue-100 text-blue-700",
  lipid_panel: "bg-purple-100 text-purple-700",
  liver_function: "bg-green-100 text-green-700",
  thyroid_function: "bg-orange-100 text-orange-700",
  other: "bg-gray-100 text-gray-700",
};

export default function RecentTests({ tests, isLoading }) {
  if (isLoading) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Recent Blood Tests
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Array(3)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="p-4 border border-slate-200 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <Heart className="w-5 h-5 text-red-500" />
          Recent Blood Tests
        </CardTitle>
        <Link to={createPageUrl("BloodTests")}>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:bg-blue-50"
          >
            View All
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {tests.slice(0, 5).map((test) => (
            <div
              key={test.id}
              className="p-4 bg-slate-50/60 rounded-xl border border-slate-200/60 hover:bg-white/80 transition-all duration-300"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-900">
                    Patient: {test.patientName || test.patientIdentificationNumber || test.patientEmail}
                  </span>
                </div>
                <Badge
                  className={`${testTypeColors[test.testType] || testTypeColors.other} border-0 font-medium`}
                >
                  {test.testType?.replace(/_/g, " ")}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(test.testDate, "MMM d, yyyy")}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <FileText className="w-4 h-4" />
                  <span>{test.labName || "Lab"}</span>
                </div>
              </div>

              {test.doctorName && (
                <div className="mt-2 text-sm text-slate-600">
                  Dr. {test.doctorName}
                </div>
              )}
            </div>
          ))}

          {tests.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Heart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No blood tests recorded yet</p>
              <Link
                to={createPageUrl("BloodTests")}
                className="inline-block mt-2"
              >
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600"
                >
                  Upload First Test
                </Button>
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
